import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../providers/AuthContext";
import { getDateRange, getFriendlyDateRangeI18nKey, getFriendlyDateRangeKey } from "../helpers/DateTime";
import useExporter from "../hooks/useExporter";

import CustomDropdown from "../components/CustomDropdown";
import DatePicker from "../components/DatePicker";
import HeaderBar from "../components/HeaderBar";
import Sidebar from "../components/Sidebar";
import FilterDimension from "../components/FilterDimension";
import LogTable, { mapHubLogToRow } from "../components/logs/LogsTable";

import toast from "react-hot-toast";

import {
    faChevronDown,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { differenceInCalendarDays, subDays } from "date-fns";

import { getAllChargingConnectorsSimplified } from "../api/ChargingConnector";
import { getLogs } from "../api/Telemetry";
import { useTranslation } from "react-i18next";

const ALL_LOG_TYPES = [
    "Status",
    "Authorization",
    "Transaction",
    "Bootnotification",
    "Heartbeat",
    "Charging-Command",
    "Change-Request",
    "Booking-Request",
    "Charge-Attempt",
];

const DEFAULT_SELECTED_LOG_TYPES = [
    "Status",
    "Authorization",
    "Transaction",
    "Bootnotification",
    "Charging-Command",
    "Change-Request",
    "Booking-Request",
    "Charge-Attempt",
];

const UI_TO_API_LOG_TYPES = {
    Status: ["ChargingPointStatus", "ChargingConnectorStatus", "NotifyEvent"],
    Authorization: ["Authorization"],
    Transaction: ["TransactionStatus"],
    Bootnotification: ["BootNotification"],
    Heartbeat: ["Heartbeat"],
    "Charging-Command": ["ChargingCommand"],
    "Change-Request": ["ChangeRequest"],
    "Booking-Request": ["BookingRequest"],
    "Charge-Attempt": ["ChargeAttempt"],
};


const FILTER_SITE = "Site";
const FILTER_POINT = "Point";
const FILTER_CONNECTOR = "Connector";

const createRawDimension = () => [
    {
        label: FILTER_SITE,
        options: [],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    },
    {
        label: FILTER_POINT,
        options: [],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    },
    {
        label: FILTER_CONNECTOR,
        options: [],
        selected: [],
        multiSelect: true,
        searchable: true,
        isActive: false,
    }
];

function LogsPage() {
    const defaultEndDate = new Date();
    const defaultStartDate = subDays(defaultEndDate, 7);

    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const { handleExport } = useExporter();

    const [dimensions, setDimensions] = useState([createRawDimension()]);

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setLogsError] = useState(false);

    const [connectors, setConnectors] = useState([]);

    const [connectorsLoaded, setConnectorsLoaded] = useState(false);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);

    const [pagination, setPagination] = useState({
        totalCount: 0,
        page: 1,
        pageSize: 100,
        totalPages: 1,
    });
    const [selectedLogTypes, setSelectedLogTypes] = useState(DEFAULT_SELECTED_LOG_TYPES);
    const normalizedSearch = search.trim();
    const dateRangeInDays =
        startDate && endDate
            ? differenceInCalendarDays(endDate, startDate)
            : 0;
    const isSearchRangeAllowed = dateRangeInDays <= 30;
    const showSearchRangeWarning = normalizedSearch.length > 0 && !isSearchRangeAllowed;

    const queryDimensions = useMemo(
        () =>
            dimensions.map((dimension) =>
                dimension.map(({ label, selected }) => ({
                    label,
                    selected: [...selected],
                }))
            ),
        [dimensions]
    );
 

    const getSelectedSiteIds = useCallback(
        (dimension) =>
            (dimension.find((x) => x.label === FILTER_SITE)?.selected ?? [])
                .map((name) =>
                    connectors.find((c) => c.chargingSiteName === name)
                        ?.chargingSitePublicId
                )
                .filter(Boolean),
        [connectors]
    );

    const updateDimension = (updatedDimension, index) => {
        setDimensions((prev) => {
            const next = [...prev];
            next[index] = updatedDimension;
            return next;
        });
        setPage(1);
    };

    const deleteDimension = (indexToRemove) => {
        setDimensions((prev) => prev.filter((_, index) => index !== indexToRemove));
        setPage(1);
    };
    const getSelectedPointIds = useCallback(
        (dimension) =>
            (dimension.find((x) => x.label === FILTER_POINT)?.selected ?? [])
                .map((name) =>
                    connectors.find((p) => p.chargingPointName === name)
                        ?.chargingPointPublicId
                )
                .filter(Boolean),
        [connectors]
    );

    const getSelectedConnectorIds = useCallback(
        (dimension) =>
            (dimension.find((x) => x.label === FILTER_CONNECTOR)?.selected ?? [])
                .map((name) =>
                    connectors.find((c) => c.chargingConnectorName === name)
                        ?.chargingConnectorPublicId
                )
                .filter(Boolean),
        [connectors]
    );

    const getSelectedErrorValues = useCallback(
        (dimension) => dimension.find((x) => x.label === "Error")?.selected ?? [],
        []
    );

    const getApiLogTypesFromDimensions = useCallback(() => {
        const apiTypes = selectedLogTypes.flatMap(
            (type) => UI_TO_API_LOG_TYPES[type] ?? []
        );
        return Array.from(new Set(apiTypes));
    }, [selectedLogTypes]);

    const buildRequest = useCallback(() => {
        const allSiteIds = new Set();
        const allPointIds = new Set();
        const allConnectorIds = new Set();
        const allErrorValues = new Set();

        queryDimensions.forEach((dimension) => {
            getSelectedSiteIds(dimension).forEach((id) => allSiteIds.add(id));
            getSelectedPointIds(dimension).forEach((id) => allPointIds.add(id));
            getSelectedConnectorIds(dimension).forEach((id) => allConnectorIds.add(id));
            getSelectedErrorValues(dimension).forEach((v) => allErrorValues.add(v));
        });

        const apiLogTypes = getApiLogTypesFromDimensions();

        const request = {
            page,
            pageSize,
            sortColumn: "createdDateTimeUtc",
            sortOrder: "desc",
            ...(apiLogTypes.length ? { logTypes: apiLogTypes } : {}),
            ...(allSiteIds.size
                ? { chargingSitePublicIds: Array.from(allSiteIds) }
                : {}),
            ...(allPointIds.size
                ? { chargingPointPublicIds: Array.from(allPointIds) }
                : {}),
            ...(allConnectorIds.size
                ? { chargingConnectorPublicIds: Array.from(allConnectorIds) }
                : {}),
            ...(isSearchRangeAllowed &&
                (normalizedSearch.length === 0 || normalizedSearch.length >= 3) &&
                normalizedSearch
                ? { search: normalizedSearch }
                : {}),
        };

        if (startDate) {
            request.startDateTime = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0,
                1
            ).toISOString();
        }

        if (endDate) {
            request.endDateTime = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                23,
                59
            ).toISOString();
        }

        const errorValues = Array.from(allErrorValues);

        if (errorValues.length) {
            const onlyYesNoUndefined = errorValues.every((x) =>
                ["Yes", "No", "Undefined"].includes(x)
            );

            if (onlyYesNoUndefined) {
                if (errorValues.includes("Yes") && !errorValues.includes("No")) {
                    request.hasError = true;
                } else if (errorValues.includes("No") && !errorValues.includes("Yes")) {
                    request.hasError = false;
                }
            } else {
                const errorCodes = errorValues.filter(
                    (x) => x !== "Undefined"
                );
                if (errorCodes.length) {
                    request.errorCodes = errorCodes;
                }
            }
        }

        return request;
    }, [
        queryDimensions,
        getSelectedSiteIds,
        getSelectedPointIds,
        getSelectedConnectorIds,
        getSelectedErrorValues,
        getApiLogTypesFromDimensions,
        page,
        pageSize,
        normalizedSearch,
        isSearchRangeAllowed,
        startDate,
        endDate,
    ]);

    const fetchGlobalLogs = useCallback(async () => {
        const token = await getAccessToken();
        const response = await getLogs(token, buildRequest());
        const rawLogs = response?.logs ?? response?.items ?? [];

        setPagination({
            totalCount: response?.totalCount ?? 0,
            page: response?.page ?? page,
            pageSize: response?.pageSize ?? pageSize,
            totalPages: response?.totalPages ?? 1,
        });

        return rawLogs
            .map(mapHubLogToRow)
            .filter(Boolean)
            .sort((a, b) => (b.dt || 0) - (a.dt || 0));
    }, [getAccessToken, buildRequest, page, pageSize]);

    const handleFetchChargingConnectors = useCallback(async () => {
        try {
            const accessToken = await getAccessToken();
            const newChargingConnectors = await getAllChargingConnectorsSimplified(accessToken);
            setConnectors(
                (newChargingConnectors ?? []).sort((a, b) =>
                    a.chargingConnectorName.localeCompare(b.chargingConnectorName)
                )
            );
            setConnectorsLoaded(true);
        } catch (error) {
            toast.error(error?.message ?? t("toast.failedFetch", { entity: t("entity.connectors") }));
            setConnectorsLoaded(true);
        }
    }, [getAccessToken, t]);

    useEffect(() => {
        handleFetchChargingConnectors();
    }, [handleFetchChargingConnectors]);

    useEffect(() => {
        setDimensions((prev) =>
            prev.map((dimension) =>
                dimension.map((filterItem) => {
                    if (filterItem.label === FILTER_SITE) {
                        const nextSiteOptions = Array.from(
                            new Set(
                                connectors
                                    .map((connector) => connector.chargingSiteName)
                                    .filter(Boolean)
                            )
                        ).sort((a, b) => a.localeCompare(b));

                        return { ...filterItem, options: nextSiteOptions };
                    }
                    return filterItem;
                })
            )
        );
    }, [connectors]);


    useEffect(() => {
        let shouldUpdate = false;

        const nextDimensions = dimensions.map((dimension) =>
            dimension.map((item) => ({
                ...item,
                selected: [...item.selected],
                options: [...item.options],
            }))
        );

        nextDimensions.forEach((dimension) => {
            const siteFilter = dimension.find((x) => x.label === FILTER_SITE);
            const pointFilter = dimension.find((x) => x.label === FILTER_POINT);
            const connectorFilter = dimension.find((x) => x.label === FILTER_CONNECTOR);

            if (siteFilter?.selected.length) {
                const dimensionPoints = siteFilter.selected.flatMap((selectedOption) => {
                    return connectors.filter(
                        (p) => p.chargingSiteName === selectedOption
                    );
                });

                const nextPointOptions = Array.from(
                    new Set(dimensionPoints.map((p) => p.chargingPointName).filter(Boolean))
                ).sort((a, b) => a.localeCompare(b));
                const sameOptions =
                    JSON.stringify(pointFilter.options) === JSON.stringify(nextPointOptions);

                if (!sameOptions) {
                    pointFilter.options = nextPointOptions;
                    pointFilter.selected = [];
                    connectorFilter.options = [];
                    connectorFilter.selected = [];
                    shouldUpdate = true;
                }
            }

            if (pointFilter?.selected.length) {
                const dimensionConnectors = pointFilter.selected.flatMap(
                    (selectedOption) => {
                    const point = connectors.find(
                            (p) => p.chargingPointName === selectedOption
                        );
                        if (!point) return [];
                        return connectors.filter(
                            (c) =>
                                c.chargingPointPublicId === point.chargingPointPublicId
                        );
                    }
                );

                const nextConnectorOptions = Array.from(
                    new Set(dimensionConnectors.map((c) => c.chargingConnectorName).filter(Boolean))
                ).sort((a, b) => a.localeCompare(b));

                const sameOptions =
                    JSON.stringify(connectorFilter.options) ===
                    JSON.stringify(nextConnectorOptions);

                if (!sameOptions) {
                    connectorFilter.options = nextConnectorOptions;
                    connectorFilter.selected = [];
                    shouldUpdate = true;
                }
            }
        });

        if (shouldUpdate) {
            setDimensions(nextDimensions);
        }
    }, [dimensions, connectors]);

    useEffect(() => {
        if (!connectorsLoaded) return;

        const normalizedSearch = search.trim();
        if (normalizedSearch.length > 0 && normalizedSearch.length < 3) {
            // Skriv inte till API, bara returnera
            return;
        }

        if (normalizedSearch.length > 0 && !isSearchRangeAllowed) {
            setLoading(false);
            setLogsError(false);
            setRows([]);
            setPagination({
                totalCount: 0,
                page: 1,
                pageSize,
                totalPages: 1,
            });
            return;
        }

        let cancelled = false;

        setLoading(true);
        setLogsError(false);

        const load = async () => {
            try {
                const mapped = await fetchGlobalLogs();
                if (cancelled) return;
                setRows(mapped);
            } catch (error) {
                console.error(error);
                if (cancelled) return;
                setLogsError(true);
                toast.error(t("toast.failedFetch", { entity: t("entity.logs") }));
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [connectorsLoaded, search, page, pageSize, startDate, endDate, selectedLogTypes, queryDimensions, isSearchRangeAllowed]);

    const getDaySelectionClass = () => {
        const rangeKey = getFriendlyDateRangeKey(startDate, endDate);

        if (rangeKey === "today" || rangeKey === "last30days" || rangeKey === "last7days") {
            return "text-primary-500 font-bold";
        }

        return "text-mute-400";
    };
    
    
    return (
        <>
            <HeaderBar />
            <div className="flex">
                <Sidebar />
                <div className="page">
                    <div className={"page-content"}>
                        <div className="flex flex-col md:flex-row justify-between md:items-center">
                            <h2 className="text-2xl text-black font-bold">{t("nav.logs")}</h2>

                            <div className="flex flex-wrap gap-2 mt-2 md:mt-0 min-w-0">
                                <CustomDropdown
                                    isRight={true}
                                    ContentComponent={() => (
                                        <div className="w-40 flex flex-col gap-2 items-start bg-white p-4 rounded-lg">
                                            <button
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    const today = new Date();
                                                    setStartDate(today);
                                                    setEndDate(today);
                                                    setPage(1);
                                                }}
                                            >
                                                {t("dateRange.today")}
                                            </button>
                                            <button
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    const lastWeek = new Date();
                                                    lastWeek.setDate(lastWeek.getDate() - 7);
                                                    setStartDate(lastWeek);
                                                    setEndDate(new Date());
                                                    setPage(1);
                                                }}
                                            >
                                                {t("dateRange.last7Days")}
                                            </button>
                                            <button
                                                className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                onClick={() => {
                                                    const lastMonth = subDays(new Date(), 30);
                                                    setStartDate(lastMonth);
                                                    setEndDate(new Date());
                                                    setPage(1);
                                                }}
                                            >
                                                {t("dateRange.last30Days")}
                                            </button>
                                        </div>
                                    )}
                                >
                                    <div className="flex gap-2">
                                        <button
                                            className={`text-sm text-black ${getDaySelectionClass()}`}
                                        >
                                            {t(getFriendlyDateRangeI18nKey(startDate, endDate))}
                                        </button>
                                    </div>
                                </CustomDropdown>

                                <CustomDropdown
                                    isRight={true}
                                    ContentComponent={() => (
                                        <DatePicker
                                            startDate={startDate}
                                            endDate={endDate}
                                            setStartDate={(d) => {
                                                setStartDate(d);
                                                setPage(1);
                                            }}
                                            setEndDate={(d) => {
                                                setEndDate(d);
                                                setPage(1);
                                            }}
                                        />
                                    )}
                                >
                                    <div className="flex gap-2">
                                        <button className="text-sm font-normal text-black">
                                            {getDateRange(startDate, endDate, t)}{" "}
                                            <FontAwesomeIcon icon={faChevronDown} />
                                        </button>
                                    </div>
                                </CustomDropdown>
                            </div>
                        </div>

                        <div className="flex gap-4 flex-col-reverse md:flex-row">
                            <div className="flex flex-col gap-4 w-full">
                                {dimensions.map((dimension, index) => (
                                    <FilterDimension
                                        key={`dimension-${index}`}
                                        dimension={dimension}
                                        setdimension={(updatedDimension) =>
                                            updateDimension(updatedDimension, index)
                                        }
                                        index={index}
                                        allowDeleteDimension={dimensions.length > 1}
                                        deleteDimension={() => deleteDimension(index)}
                                    />
                                ))}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    className="btn-sm btn-outline-primary"
                                    onClick={() => handleExport(rows ?? [], "logs")}
                                >
                                    {t("common.export")}
                                </button>
                            </div>
                        </div>

                        <div className="rounded-md shadow-md p-4">
                            <LogTable
                                rows={rows}
                                loading={loading}
                                pagination={pagination}
                                allLogTypes={ALL_LOG_TYPES}
                                selectedLogTypes={selectedLogTypes}
                                onToggleLogType={(type) => {
                                    setPage(1);

                                    setSelectedLogTypes(prev => {
                                        const exists = prev.includes(type);
                                        return exists
                                            ? prev.filter(x => x !== type)
                                            : [...prev, type];
                                    });
                                }}
                                onPageChange={(nextPage) => setPage(nextPage)}
                                onPageSizeChange={(nextPageSize) => {
                                    setPageSize(nextPageSize);
                                    setPage(1);
                                }}
                                search={search}
                                onSearchChange={(value) => {
                                    setSearch(value);
                                    setPage(1);
                                }}
                                minSearchLength={3}
                                showSearchRangeWarning={showSearchRangeWarning}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LogsPage;
